
<template lang="html">
  <item-editor-dialog
    :id="selectedItemId"
    ref="editForm"
    v-model="selectedPanel"
    :show="showItem"
    :clone-id="cloneId"
    :title="pageTitle"
    :on-close="doClose"
    :context-menu="eventContextMenu"
    :lock-timer="lockTimerSettings"
    :loading="isDataLoading"
    :errors="errors"
    :edit-panel-id="editPanel"
    :audit-panel-id="auditPanel"
    :audit="auditHistorySettings"
    :buttons="pageBarButtons"
  >
    <!-- CASCADE CHANGES DIALOG -->
    <v-dialog
      v-model="showEventGroupSaveDialog"
      :max-width="350"
      scrollable
    >
      <v-card>
        <v-card-title class="pa-0">
          <div class="px-3 pt-3 pb-1 headline">
            Save Changes To
          </div>
          <v-tabs
            ref="saveEventGroupModeTabs"
            v-model="saveEventGroupModeTab"
            :light="!$ftAppIsDark"
            :dark="$ftAppIsDark"
            :color="$ftStyle.pageBarTabs"
            show-arrows
            grow
            style="min-width: 100%"
          >
            <v-tabs-slider :color="$ftStyle.pageBarTabsSlider" />
            <v-tab>This Event Only</v-tab>
            <v-tab>Others In Group</v-tab>
          </v-tabs>
        </v-card-title>
        <v-card-text class="pa-2">
          <v-tabs-items v-model="saveEventGroupModeTab">
            <v-tab-item>
              <!-- TODO: Make a better message -->
              Warning: Saving this recurring event without cascading changes
              will isolate it from other similar events in the group.
            </v-tab-item>
            <v-tab-item>
              <v-list
                two-line
                subheader
              >
                <v-subheader>Select Events to cascade changes to</v-subheader>
                <v-list-item
                  v-for="(event, i) of eventsInGroup"
                  :key="i"
                  :disabled="event.disabled && event.id !== itemData.id"
                >
                  <v-list-item-action>
                    <v-checkbox
                      v-model="event.__selected"
                      :disabled="event.disabled"
                    />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title :class="{'primary--text': event.id === itemData.id}">
                      {{ event.date }}
                    </v-list-item-title>
                    <v-list-item-subtitle>{{ event.name }}: {{ displayTime(event.startTime) }} to {{ displayTime(event.endTime) }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click.native="showEventGroupSaveDialog = false"
          >
            Close
          </v-btn>
          <v-btn
            class="blue--text darken-1"
            text
            @click.native="saveEventByGroupMode"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ITEM DETAIL -->

    <!-- START OF FORM FIELDS -->
    <v-flex class="pt-3" />

    <expansion-card icon="mdi-calendar">
      <span slot="title">Event</span>
      <v-layout
        wrap
      >
        <v-flex class="xs12 sm6 md6 px-3">
          <v-text-field
            v-model.trim="itemData.name"
            :label="type + ' Name'"
            :rules="rules().required('Name')"
            :error-messages="getPathErrors(jsonPath, 'name')"
            :disabled="isFormDisabled"
            required
            @input="clearPathErrors(jsonPath, 'name')"
          />
        </v-flex>
        <v-flex class="xs12 sm6 md6 px-3">
          <date-picker
            v-model.trim="itemData.date"
            :label="'Date'"
            :min="minimumEventDate"
            :max="maxSelectDate"
            :rules="rules().required()"
            :error-messages="getPathErrors(jsonPath, 'date')"
            :disabled="isFormDisabled"
            :color="selectedDateIsInPast ? 'warning' : undefined"
            :input-class="{'ft-warning-input': selectedDateIsInPast}"
            :hint="selectedDateIsInPastMessage"
            :persistent-hint="selectedDateIsInPast"
            required
            @input="popStartEndDates"
          />
        </v-flex>
      </v-layout>

      <v-layout
        wrap
      >
        <v-flex class="xs12 sm6 md6 px-3">
          <time-picker
            v-model.trim="itemData.startTime"
            :label="'Start Time'"
            :rules="rules().required()"
            :error-messages="getPathErrors(jsonPath, 'startTime')"
            :disabled="isFormDisabled"
            :min-interval="5"
            required
            @input="clearPathErrors(jsonPath, 'startTime')"
          />
        </v-flex>
        <v-flex class="xs12 sm6 md6 px-3">
          <time-picker
            v-model.trim="itemData.endTime"
            :label="'End Time'"
            :rules="rules().required()"
            :error-messages="getPathErrors(jsonPath, 'endTime')"
            :disabled="isFormDisabled"
            :min-interval="5"
            required
            @input="clearPathErrors(jsonPath, 'endTime')"
          />
        </v-flex>
      </v-layout>


      <v-layout
        wrap
      >
        <v-flex class="xs12 sm6 md6 px-3">
          <div style="display: flex; flex-wrap: nowrap;">
            <date-picker
              v-model="itemData.maxAdvanceDays"
              label="Open For Reservations"
              style="flex: 1 1 auto;"
              :offset-from="itemData.date"
              :max="itemData.date"
              :rules="rules().required()"
              required
              :error-messages="getPathErrors(jsonPath, 'maxAdvanceDays')"
              :class="popStartEnd"
              :disabled="!itemData.date || isFormDisabled"
              @input="clearPathErrors(jsonPath, 'maxAdvanceDays')"
            />
            <time-picker
              v-model="itemData.reservationsOpenTime"
              class="ml-3"
              style="flex: 0 0 120px;"
              label="at"
              :rules="rules().required()"
              :error-messages="getPathErrors(jsonPath, 'reservationsOpenTime')"
              :disabled="!itemData.date || isFormDisabled"
              required
              @input="clearPathErrors(jsonPath, 'reservationsOpenTime')"
            />
          </div>
        </v-flex>
        <v-flex class="xs12 sm6 md6 px-3">
          <div style="display: flex; flex-wrap: nowrap;">
            <date-picker
              v-model.trim="itemData.minAdvanceDays"
              label="Close For Reservations"
              style="flex: 1 1 auto;"
              :offset-from="itemData.date"
              :max="itemData.date"
              :rules="rules().required()"
              required
              :error-messages="getPathErrors(jsonPath, 'minAdvanceDays')"
              :class="popStartEnd"
              :disabled="!itemData.date || isFormDisabled"
              @input="clearPathErrors(jsonPath, 'minAdvanceDays')"
            />
            <time-picker
              v-model.trim="itemData.reservationsCloseTime"
              class="ml-3"
              style="flex: 0 0 120px;"
              label="at"
              :rules="rules().required()"
              :error-messages="getPathErrors(jsonPath, 'reservationsCloseTime')"
              :disabled="!itemData.date || isFormDisabled"
              required
              @input="clearPathErrors(jsonPath, 'reservationsCloseTime')"
            />
          </div>
        </v-flex>
      </v-layout>

      <v-divider class="my-3" />

      <v-layout
        wrap
      >
        <v-flex class="xs12 sm6 md6 px-3">
          <v-autocomplete
            v-model="itemData.eventTypeId"
            label="Event Type"
            auto
            placeholder="Select Type"
            :items="eventTypes"
            :rules="rules().required()"
            required
            :error-messages="getPathErrors(jsonPath, 'eventTypeId')"
            :disabled="isFormDisabled"
            item-text="name"
            item-value="id"
            @input="clearPathErrors(jsonPath, 'eventTypeId')"
          />
        </v-flex>
        <v-flex class="xs12 sm6 md6 px-3">
          <v-text-field
            v-model.trim="itemData.banquetEventOrder"
            label="Banquet Event Order Number"
            :error-messages="getPathErrors(jsonPath, 'banquetEventOrder')"
            :disabled="isFormDisabled"
            @input="clearPathErrors(jsonPath, 'banquetEventOrder')"
          />
        </v-flex>
      </v-layout>

      <v-layout
        wrap
      >
        <v-flex class="xs12 sm6 md6 px-3">
          <v-autocomplete
            v-model="itemData.eventCategoryId"
            label="Category"
            auto
            placeholder="Select Category"
            :items="eventCategories"
            :rules="rules().required()"
            required
            :error-messages="getPathErrors(jsonPath, 'eventCategoryId')"
            :disabled="isFormDisabled"
            item-text="name"
            item-value="id"
            @input="clearPathErrors(jsonPath, 'eventCategoryId')"
          />
        </v-flex>
        <v-flex class="xs12 sm6 md6 px-3">
          <v-layout row>
            <v-flex
              class="pr-3"
              style="max-width: 50%"
            >
              <v-text-field
                v-model.trim="itemData.maxPartySize"
                label="Max Party Size"
                auto
                type="number"
                :rules="[
                  ...rules().required('Max Party Size'),
                  v => {return v <= 100 || 'Value must 100 or less'}
                ]"
                required
                :error-messages="getPathErrors(jsonPath, 'maxPartySize')"
                :disabled="isFormDisabled"
                @blur="itemData.maxPartySize = forceInt(itemData.maxPartySize)"
                @input="clearPathErrors(jsonPath, 'maxPartySize')"
              />
            </v-flex>
            <v-flex>
              <v-text-field
                v-model.trim="itemData.maxPartySizeStaff"
                label="Max Staff Party Size"
                auto
                type="number"
                :rules="rules().required()"
                required
                :error-messages="getPathErrors(jsonPath, 'maxPartySizeStaff')"
                :disabled="isFormDisabled"
                @blur="itemData.maxPartySizeStaff = forceInt(itemData.maxPartySizeStaff)"
                @input="clearPathErrors(jsonPath, 'maxPartySizeStaff')"
              />
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </expansion-card>

    <v-divider class="my-3" />

    <expansion-card icon="mdi-timetable">
      <span slot="title">Seating</span>
      <v-layout
        wrap
      >
        <v-flex
          class="px-3"
          :class="{'xs12 sm6 md6': getTimeFormat(itemData.timeFormatId).code === 'ongoing'}"
        >
          <v-autocomplete
            v-model="itemData.timeFormatId"
            label="Time Format"
            auto
            :items="timeFormats"
            :rules="rules().required()"
            required
            :error-messages="getPathErrors(jsonPath, 'timeFormatId')"
            :disabled="isFormDisabled"
            item-text="name"
            item-value="id"
            @input="clearPathErrors(jsonPath, 'timeFormatId'); clearPathErrors(jsonPath, 'seatings'); clearPathErrors(jsonPath, 'ongoingPeriodId'); clearPathErrors(jsonPath, 'firstReservationTime'); clearPathErrors(jsonPath, 'lastReservationTime')"
          />
        </v-flex>
        <v-flex
          v-if="getTimeFormat(itemData.timeFormatId).code === 'ongoing'"
          class="xs12 sm6 md6 px-3"
        >
          <v-autocomplete
            v-model="itemData.ongoingPeriodId"
            label="Seating Interval"
            auto
            :items="periods.filter(period => !!period.interval)"
            :rules="rules().required()"
            required
            :error-messages="getPathErrors(jsonPath, 'ongoingPeriodId')"
            :disabled="isFormDisabled"
            item-text="name"
            item-value="id"
            @input="clearPathErrors(jsonPath, 'ongoingPeriodId');"
          />
        </v-flex>
      </v-layout>

      <v-flex
        v-if="getTimeFormat(itemData.timeFormatId).code === 'multiple'"
        class="px-3"
      >
        <detail-editor-table
          v-model="itemData.seatings"
          type="Seating"
          :json-path="buildJsonPath('seatings')"
          :get-path-errors="getPathErrors"
          :clear-path-errors="clearPathErrors"
          :move-path-errors="movePathErrors"
          :disabled="isFormDisabled"
          :item-default="{
            id: null,
            eventId: itemData.id,
            time: '',
            allowMemberMade: true,
            disabled: false
          }"
          :headers="[
            headerEditor({label: 'Time', field: 'time', editor: {type: 'slot'}}),
            headerEditor({label: 'Visible To Members', field: 'allowMemberMade', editor: {type: 'switch'}, thin: true}),
            headerEditor({label: 'Enabled', field: 'disabled', editor: {type: 'switch', invert: true}, thin: true})
          ]"
          @error="addError"
          @add="resetItemFieldErrorLookup"
        >
          <template
            slot="time"
            slot-scope="props"
          >
            <time-picker
              v-model.trim="props.item.time"
              :rules="rules().required()"
              :error-messages="props.errorMessages"
              :disabled="isFormDisabled"
              :min-interval="5"
              required
              @input="props.errorMessages._clear()"
            />
          </template>
        </detail-editor-table>
      </v-flex>

      <v-layout
        v-else-if="getTimeFormat(itemData.timeFormatId).code === 'ongoing'"
        wrap
      >
        <v-flex class="xs12 sm6 md6 px-3">
          <time-picker
            v-model.trim="itemData.firstReservationTime"
            :label="'First Seating Time'"
            :rules="rules().required()"
            :error-messages="getPathErrors(jsonPath, 'firstReservationTime')"
            :disabled="isFormDisabled"
            :min-interval="5"
            required
            @input="clearPathErrors(jsonPath, 'firstReservationTime'); clearPathErrors(jsonPath, 'lastReservationTime')"
          />
        </v-flex>
        <v-flex class="xs12 sm6 md6 px-3">
          <time-picker
            v-model.trim="itemData.lastReservationTime"
            :label="'Last Seating Time'"
            :rules="rules().required()"
            :error-messages="getPathErrors(jsonPath, 'lastReservationTime')"
            :disabled="isFormDisabled"
            :min-interval="5"
            required
            @input="clearPathErrors(jsonPath, 'lastReservationTime'); clearPathErrors(jsonPath, 'firstReservationTime')"
          />
        </v-flex>
      </v-layout>

      <v-layout
        wrap
      >
        <v-flex class="xs12 sm6 md6 px-3">
          <v-text-field
            v-model.trim="itemData.tableTurnRate"
            prepend-icon="mdi-repeat"
            label="Table Turn Rate"
            auto
            type="number"
            clearable
            placeholder="Turn with seating"
            hint="Average table turn rate in minutes. (Empty = turn with seating.)"
            persistent-hint
            :error-messages="getPathErrors(jsonPath, 'tableTurnRate')"
            :disabled="isFormDisabled"
            @blur="itemData.tableTurnRate = forceInt(itemData.tableTurnRate) || null"
            @input="clearPathErrors(jsonPath, 'tableTurnRate')"
          />
        </v-flex>
      </v-layout>
    </expansion-card>

    <v-divider class="my-3" />

    <expansion-card icon="mdi-cancel">
      <span slot="title">Cancellations</span>
      <v-layout
        wrap
      >
        <v-flex class="xs12 sm6 md6 px-3">
          <div style="display: flex; flex-wrap: nowrap;">
            <v-text-field
              v-model="itemData.cancellationTime"
              label="Apply cancellation charge within"
              type="number"
              :error-messages="getPathErrors(jsonPath, 'cancellationTime')"
              :disabled="isFormDisabled"
              @blur="itemData.cancellationTime = forceInt(itemData.cancellationTime)"
            />
            <v-autocomplete
              v-model="itemData.cancellationTimeIntervalId"
              class="ml-3"
              style="flex: 0 0 100px;"
              label="Interval"
              auto
              :items="cancellationTimeIntervals"
              :error-messages="getPathErrors(jsonPath, 'cancellationTimeIntervalId')"
              :disabled="isFormDisabled"
              item-text="name"
              item-value="id"
            />
          </div>
        </v-flex>
        <v-flex
          v-if="itemData.cancellationTime"
          class="xs12 sm6 md6 px-3"
        >
          <div style="display: flex; flex-wrap: nowrap;">
            <v-autocomplete
              v-model="itemData.cancellationChargeTypeId"
              class="mr-3"
              style="flex: 0 0 160px;"
              label="Charge Type"
              auto
              :items="cancellationChargeTypes"
              :error-messages="getPathErrors(jsonPath, 'cancellationChargeTypeId')"
              :disabled="isFormDisabled"
              item-text="name"
              item-value="id"
              @input="clearPathErrors(jsonPath, 'cancellationChargeTypeId')"
            />
            <formatted-input
              v-if="cancellationChargeType.code === 'flat'"
              key="chrg_flat"
              v-model.trim="itemData.cancellationCharge"
              label="Cancellation Charge"
              type="number"
              prefix="$"
              step="0.01"
              :blurred-format="forceCurrency"
              :error-messages="getPathErrors(jsonPath, 'cancellationCharge')"
              :disabled="isFormDisabled || (enablePOSChargeIntegration && !!itemData.cancellationChargeCode)"
              @blur="itemData.cancellationCharge = forceCurrency(itemData.cancellationCharge)"
              @input="clearPathErrors(jsonPath, 'cancellationCharge')"
            />
            <formatted-input
              v-else-if="cancellationChargeType.code === 'percent'"
              key="chrg_percent"
              ref="cancelCharge"
              v-model.trim="cancellationChargePercentage"
              label="Cancellation Charge"
              type="number"
              prefix="%"
              :error-messages="getPathErrors(jsonPath, 'cancellationCharge')"
              :disabled="isFormDisabled"
              @input="clearPathErrors(jsonPath, 'cancellationCharge')"
            />
            <formatted-input
              v-else
              key="chrg_unset"
              v-model.trim="itemData.cancellationCharge"
              label="Cancellation Charge"
              type="number"
              prefix="?"
              step="0.01"
              :error-messages="getPathErrors(jsonPath, 'cancellationCharge')"
              :disabled="isFormDisabled"
              @input="clearPathErrors(jsonPath, 'cancellationCharge')"
            />
            <v-autocomplete
              v-if="enablePOSChargeIntegration && cancellationChargeType.code === 'flat'"
              v-model="itemData.cancellationChargeCode"
              label="Cancellation Charge Code"
              :items="posMenuItems"
              item-text="name"
              item-value="sku"
              clearable
              auto
              :error-messages="getPathErrors(jsonPath, 'cancellationChargeCode')"
              :disabled="isFormDisabled"
              @input="onCancellationChargeCodeSelect"
            />

            <formatted-input
              v-else-if="!enablePOSChargeIntegration && cancellationChargeType.code === 'flat'"
              v-model.trim="itemData.cancellationChargeCode"
              label="Cancellation Charge Code"
              type="text"
              auto
              :error-messages="getPathErrors(jsonPath, 'cancellationChargeCode')"
              :disabled="isFormDisabled"
              @input="clearPathErrors(jsonPath, 'cancellationChargeCode')"
            />
          </div>
        </v-flex>
        <v-flex
          v-if="cancellationPercentPosWarning"
          class="xs12 px-3 pt-2"
        >
          <div style="font-size: 12px; color: rgba(0, 0, 0, 0.6);">
            Percent of Price charge type cancellation charges cannot be sent to POS.
          </div>
        </v-flex>
      </v-layout>
    </expansion-card>

    <v-divider class="my-3" />

    <detail-editor-table
      v-model="itemData.costs"
      type="Price"
      :json-path="buildJsonPath('costs')"
      :get-path-errors="getPathErrors"
      :clear-path-errors="clearPathErrors"
      :move-path-errors="movePathErrors"
      :disabled="isFormDisabled"
      :delete-item-check="(item, index) => !item.useCount"
      sortable
      :item-default="{
        id: null,
        priceTypeId: null,
        eventId: itemData.id,
        chargeCode: null,
        hidePrice: false,
        pos: 0,
        allowMemberMade: true
      }"
      :headers="priceHeaders"
    >
      <template
        slot="priceTypeId"
        slot-scope="props"
      >
        <v-autocomplete
          v-model="props.item.priceTypeId"
          placeholder="Select Type"
          :items="priceTypes"
          :rules="rules().required()"
          required
          auto
          :error-messages="props.errorMessages"
          :disabled="isFormDisabled"
          item-text="name"
          item-value="id"
          @input="props.errorMessages._clear()"
        />
      </template>
      <template
        slot="price"
        slot-scope="props"
      >
        <formatted-input
          v-model.trim="props.item.price"
          label="Price"
          type="number"
          prefix="$"
          step="0.01"
          :blurred-format="forceCurrency"
          :disabled="isFormDisabled || (enablePOSChargeIntegration && !!props.item.chargeCode)"
          :error-messages="props.errorMessages"
          @blur="props.item.price = forceCurrency(props.item.price)"
          @input="props.errorMessages._clear()"
        />
      </template>
      <template
        slot="useCount"
        slot-scope="props"
      >
        <div>{{ props.item.useCount ? props.item.useCount : '-' }}</div>
      </template>
      <template
        slot="chargeCode"
        slot-scope="props"
      >
        <v-autocomplete
          v-model="props.item.chargeCode"
          :items="posMenuItems"
          item-text="name"
          item-value="sku"
          clearable
          auto
          :disabled="isFormDisabled"
          :error-messages="props.errorMessages"
          @input="val => {
            const posItem = posMenuItems.find(i => i.sku === val)
            props.item.price = posItem ? forceCurrency(posItem.memberPrice) : null
            props.errorMessages._clear()
          }"
        />
      </template>
    </detail-editor-table>

    <v-divider class="my-3" />

    <detail-editor-table
      v-model="itemData.locations"
      type="Location"
      :json-path="buildJsonPath('locations')"
      :get-path-errors="getPathErrors"
      :clear-path-errors="clearPathErrors"
      :move-path-errors="movePathErrors"
      :disabled="isFormDisabled"
      :disable-add="!hasAvailLocation"
      sortable
      required
      :item-default="{
        id: null,
        locationId: null,
        eventId: itemData.id,
        memberRestrictionId: null,
        tableConfigId: null,
        requireTableAssignment: false,
        autoAssignTables: false,
        isBlocker: false,
        maxMemberOrigSize: null,
        blockMinBefore: null,
        blockMinAfter: null,
        covers: null,
        pos: 0
      }"
      :headers="[
        headerDragSort(),
        headerEditor({label: 'Location', field: 'locationId', editor: {type: 'slot'}}),
        headerEditor({label: 'Member Restriction', field: 'memberRestrictionId', editor: {type: 'slot'}}),
        ...(allowAutoAssign ? [
          headerEditor({label: 'Table Config', field: 'tableConfigId', editor: {type: 'slot'}}),
          headerEditor({label: 'Require Table Assignment', field: 'requireTableAssignment', editor: {type: 'switch', hideIf: item => !item.tableConfigId}, thin: true}),
          headerEditor({label: 'Auto Assign Tables', field: 'autoAssignTables', editor: {type: 'switch', hideIf: item =>
            !tableConfigs.find(tc => tc.id === item.tableConfigId && tc.locationId === item.locationId && tc.autoAssignable)
          }, thin: true}),
        ] : []),
        headerEditor({label: 'Covers per Seating', field: 'covers', editor: {type: 'slot'}, thin: true}),
        headerEditor({label: 'Max Member Made', field: 'maxMemberOrigSize', editor: {type: 'slot'}, thin: true}),
        headerEditor({label: 'Is Blocker', field: 'isBlocker', editor: {type: 'switch'}, thin: true}),
        headerEditor({label: 'Block Minutes Before', field: 'blockMinBefore', editor: {type: 'slot'}, thin: true}),
        headerEditor({label: 'Block Minutes After', field: 'blockMinAfter', editor: {type: 'slot'}, thin: true})
      ]"
      @error="addError"
      @add="resetItemFieldErrorLookup"
    >
      <template
        slot="locationId"
        slot-scope="props"
      >
        <v-autocomplete
          v-model="props.item.locationId"
          placeholder="Select Location"
          :items="locations.filter((location) => {
            if (location.id === props.item.locationId) return true
            for (let i = 0, l = itemData.locations.length; i < l; i += 1) {
              if (itemData.locations[i].locationId === location.id) return false
            }
            return true
          })"
          :rules="rules().required()"
          required
          auto
          :error-messages="props.errorMessages"
          :disabled="isFormDisabled"
          item-text="name"
          item-value="id"
          @input="props.errorMessages._clear()"
        />
      </template>
      <template
        slot="memberRestrictionId"
        slot-scope="props"
      >
        <v-autocomplete
          v-model="props.item.memberRestrictionId"
          label="Member Restriction"
          :placeholder="memberRestrictions[0] ? 'Select Member Restriction' : 'No Applicable Member Restrictions'"
          :items="memberRestrictions"
          auto
          clearable
          :error-messages="props.errorMessages"
          :disabled="isFormDisabled"
          item-text="name"
          item-value="id"
          @change="props.errorMessages._clear()"
        />
      </template>
      <template
        slot="tableConfigId"
        slot-scope="props"
      >
        <v-autocomplete
          :placeholder="tableConfigs.filter(tc => tc.locationId === props.item.locationId && ((!tc.disabled && tc.allowEvent) || tc.id === props.item.tableConfigId))[0] ? 'Select Table Configuration' : 'No Applicable Table Configuration'"
          :items="tableConfigs.filter(tc => tc.locationId === props.item.locationId && ((!tc.disabled && tc.allowEvent) || tc.id === props.item.tableConfigId))"
          :value="props.item.tableConfigId"
          clearable
          auto
          :error-messages="props.errorMessages"
          :disabled="isFormDisabled"
          item-text="name"
          item-value="id"
          @change="props.setCellValue(props.item, props.header.value, props.header.editor, props.row, $event)"
        />
      </template>
      <template
        slot="covers"
        slot-scope="props"
      >
        <v-text-field
          v-model.trim="props.item.covers"
          :rules="props.item.isBlocker ? [] : rules().required()"
          type="number"
          class="ft-small-input"
          :error-messages="props.errorMessages"
          :disabled="isFormDisabled || props.item.isBlocker"
          required
          @blur="props.item.covers = forceInt(props.item.covers)"
          @input="props.errorMessages._clear()"
        />
      </template>
      <template
        slot="maxMemberOrigSize"
        slot-scope="props"
      >
        <v-text-field
          v-model.trim="props.item.maxMemberOrigSize"
          :rules="props.item.isBlocker ? [] : rules().required()"
          type="number"
          class="ft-small-input"
          :error-messages="props.errorMessages"
          :disabled="isFormDisabled || props.item.isBlocker"
          required
          @blur="props.item.maxMemberOrigSize = forceInt(props.item.maxMemberOrigSize)"
          @input="props.errorMessages._clear()"
        />
      </template>
      <template
        slot="blockMinBefore"
        slot-scope="props"
      >
        <v-text-field
          v-model.trim="props.item.blockMinBefore"
          type="number"
          class="ft-small-input"
          :error-messages="props.errorMessages"
          :disabled="isFormDisabled"
          @blur="props.item.blockMinBefore = forceInt(props.item.blockMinBefore)"
          @input="props.errorMessages._clear()"
        />
      </template>
      <template
        slot="blockMinAfter"
        slot-scope="props"
      >
        <v-text-field
          v-model.trim="props.item.blockMinAfter"
          type="number"
          class="ft-small-input"
          :error-messages="props.errorMessages"
          :disabled="isFormDisabled"
          @blur="props.item.blockMinAfter = forceInt(props.item.blockMinAfter)"
          @input="props.errorMessages._clear()"
        />
      </template>
    </detail-editor-table>

    <v-divider class="my-3" />

    <question-editor
      v-model="itemData.questions"
      :json-path="buildJsonPath('questions')"
      :get-path-errors="getPathErrors"
      :clear-path-errors="clearPathErrors"
      :move-path-errors="movePathErrors"
      :event-id="itemData.id"
      :disabled="isFormDisabled"
      :discrete-covers="itemData.discreteCovers"
      @add="resetItemFieldErrorLookup"
    />

    <v-divider class="my-3" />

    <expansion-card icon="mdi-tie">
      <span slot="title">Event Style</span>
      <v-layout
        wrap
      >
        <v-flex class="xs12 sm6 md6 px-3">
          <v-autocomplete
            v-model="itemData.dressCodeId"
            label="Dress Code"
            auto
            :items="dressCodes"
            clearable
            :open-on-clear="false"
            :error-messages="getPathErrors(jsonPath, 'dressCodeId')"
            :disabled="isFormDisabled"
            item-text="name"
            item-value="id"
            @input="clearPathErrors(jsonPath, 'dressCodeId')"
            @click:clear="$nextTick(() => itemData.dressCodeId = null)"
          />
        </v-flex>
        <v-flex class="xs12 sm6 md6 px-3">
          <v-autocomplete
            v-model="itemData.musicalStyleId"
            label="Musical Style"
            auto
            :items="musicalStyles"
            clearable
            :open-on-clear="false"
            :error-messages="getPathErrors(jsonPath, 'musicalStyleId')"
            :disabled="isFormDisabled"
            item-text="name"
            item-value="id"
            @input="clearPathErrors(jsonPath, 'musicalStyleId')"
            @click:clear="$nextTick(() => itemData.musicalStyleId = null)"
          />
        </v-flex>
      </v-layout>

      <v-layout
        wrap
      >
        <v-flex class="xs12 sm6 md6 px-3">
          <v-text-field
            v-model.trim="itemData.theme"
            label="Theme"
            :error-messages="getPathErrors(jsonPath, 'theme')"
            :disabled="isFormDisabled"
            @input="clearPathErrors(jsonPath, 'theme')"
          />
        </v-flex>
        <v-flex class="xs12 sm6 md6 px-3">
          <v-text-field
            v-model.trim="itemData.extras"
            label="Extras"
            :error-messages="getPathErrors(jsonPath, 'extras')"
            :disabled="isFormDisabled"
            @input="clearPathErrors(jsonPath, 'extras')"
          />
        </v-flex>
      </v-layout>
      <v-flex class="px-3">
        <v-textarea
          v-model.trim="itemData.notes"
          label="Internal Staff Notes"
          :rows="1"
          auto-grow
          :error-messages="getPathErrors(jsonPath, 'notes')"
          :disabled="isFormDisabled"
          @input="clearPathErrors(jsonPath, 'notes')"
        />
      </v-flex>
    </expansion-card>

    <v-divider class="my-3" />

    <expansion-card icon="mdi-format-list-checks">
      <span slot="title">Event Itinerary</span>
      <v-flex class="px-3">
        <markup-editor
          v-model.trim="itemData.onlineMessage"
          :rules="rules().maxLen(1000000, 'Message too large.  Try using smaller images or attachments.')"
          :error-messages="getPathErrors(jsonPath, 'onlineMessage')"
          :disabled="isFormDisabled"
          @input="clearPathErrors(jsonPath, 'onlineMessage')"
        />
      </v-flex>
    </expansion-card>

    <v-divider class="my-3" />

    <expansion-card icon="mdi-cog">
      <span slot="title">Options</span>

      <v-flex
        class="px-3 pb-3"
      >
        <v-boolean
          v-model="itemData.ignoreConflictingReservations"
          label="Ignore Conflicting Reservations"
          :error-messages="getPathErrors(jsonPath, 'ignoreConflictingReservations')"
          :field-errors="getPathErrors(jsonPath, 'ignoreConflictingReservations')"
          :disabled="isFormDisabled"
        />

        <v-boolean
          v-model="itemData.allowMemberReservations"
          label="Allow Members to Make Reservations"
          :error-messages="getPathErrors(jsonPath, 'allowMemberReservations')"
          :field-errors="getPathErrors(jsonPath, 'allowMemberReservations')"
          :disabled="isFormDisabled"
        />

        <!-- BLOCK MEMBER EDIT -->
        <v-boolean
          v-model="itemData.blockMemberEdit"
          auto-inline
          label="Block Member Edit Access"
          :error-messages="getPathErrors(jsonPath, 'blockMemberEdit')"
          :field-errors="getPathErrors(jsonPath, 'blockMemberEdit')"
          :disabled="isFormDisabled"
        />

        <transition
          name="fadeheight"
          mode="out-in"
        >
          <template v-if="!itemData.allowMemberReservations">
            <v-textarea
              v-model.trim="itemData.noMemberAccessMessage"
              label="No Member Access Message"
              :rows="1"
              auto-grow
              :error-messages="getPathErrors(jsonPath, 'noMemberAccessMessage')"
              :disabled="isFormDisabled"
              @input="clearPathErrors(jsonPath, 'noMemberAccessMessage')"
            />
          </template>
        </transition>


        <bound-property-selector
          v-model="itemData.blockedGuestTypes"
          :items="guestTypes"
          label="Restricted Guest Types"
          hint="Members will not be able to use selected guest types"
          persistent-hint
          property-id-field="guestTypeId"
          parent-id-field="eventId"
          item-id-field="id"
          :parent-id="itemData.id"
          name-field="name"
        />
        <!-- <v-autocomplete
          v-model="itemData.guests"
          label="Guests"
          auto
          :items="guestAllowSelection"
          :error-messages="getPathErrors(jsonPath, 'guests')"
          :disabled="isFormDisabled"
          :persistent-hint="!!(guestAllowSelection.find(g => g.value === itemData.guests) || {}).hint"
          :hint="(guestAllowSelection.find(g => g.value === itemData.guests) || {}).hint"
          @input="clearPathErrors(jsonPath, 'guests')"
        /> -->

        <template>
          <v-boolean
            v-model="itemData.hideFromMembers"
            label="Hide From Members"
            :error-messages="getPathErrors(jsonPath, 'hideFromMembers')"
            :field-errors="getPathErrors(jsonPath, 'hideFromMembers')"
            :disabled="isFormDisabled"
          />
        </template>

        <v-boolean
          v-show="false"
          v-model="itemData.childrenAllowed"
          label="Allow Children"
          :error-messages="getPathErrors(jsonPath, 'childrenAllowed')"
          :field-errors="getPathErrors(jsonPath, 'childrenAllowed')"
          :disabled="isFormDisabled"
        />

        <v-boolean
          v-model="itemData.discreteCovers"
          label="Require Full Details for Every Reservee"
          :error-messages="getPathErrors(jsonPath, 'discreteCovers')"
          :field-errors="getPathErrors(jsonPath, 'discreteCovers')"
          :disabled="isFormDisabled"
        />

        <v-boolean
          v-model="itemData.lottery"
          label="Lottery Event"
          :error-messages="getPathErrors(jsonPath, 'lottery')"
          :field-errors="getPathErrors(jsonPath, 'lottery')"
          :disabled="isFormDisabled"
        />

        <v-boolean
          v-if="enablePOSChargeIntegration"
          v-model="sendNonMemberTicketsToPOS"
          label="Send Non Member Tickets to POS"
          :disabled="isFormDisabled"
        />

        <v-autocomplete
          v-if="sendNonMemberTicketsToPOS && enablePOSChargeIntegration"
          v-model="itemData.guestAccountToCharge"
          label="Guest Charge Account"
          auto
          :items="members"
          clearable
          item-text="nameFull"
          item-value="id"
          :error-messages="getPathErrors(jsonPath, 'guestAccountToCharge')"
          :disabled="isFormDisabled"
          @input="clearPathErrors(jsonPath, 'guestAccountToCharge')"
        />

        <v-boolean
          v-model="itemData.cancelled"
          label="Cancelled"
          :error-messages="getPathErrors(jsonPath, 'cancelled')"
          :field-errors="getPathErrors(jsonPath, 'cancelled')"
          :disabled="isFormDisabled"
        />
      </v-flex>
    </expansion-card>

    <!-- END OF FORM FIELDS -->
  </item-editor-dialog>
</template>

<script>
import {helpers, itemEdit, withChildren, MarkupEditor} from '@/ft-vue-core'
import moment from 'moment'
import {cloner} from './util/values'
import QuestionEditor from '@/components/common/QuestionEditor'
import BoundPropertySelector from '../common/BoundPropertySelector'

const {displayTime, displayDate} = helpers

var itemDefault = {
  id: null,
  name: null,
  date: null,
  eventGroupId: null,
  eventCategoryId: null,
  eventTypeId: null,
  timeFormatId: null,
  ongoingPeriodId: null,
  dressCodeId: null,
  musicalStyleId: null,
  // memberRestrictionId: null,
  startTime: null,
  endTime: null,
  firstReservationTime: null,
  lastReservationTime: null,
  blockedGuestTypes: [],
  blockMemberEdit: false,
  // guests: null,
  // cancellationDays: null, // Not used (need to remove from DiningEvent.java and dining_events table)
  cancellationTime: null,
  cancellationTimeIntervalId: 1,
  cancellationCharge: null,
  cancellationChargeTypeId: null,
  cancellationChargeCode: null,
  maxPartySize: null,
  maxPartySizeStaff: null,
  minAdvanceDays: 0,
  maxAdvanceDays: 0,
  reservationsOpenTime: '00:00:00',
  reservationsCloseTime: '23:59:00',
  tableTurnRate: null,
  childrenAllowed: true,
  openDated: false,
  allowMemberReservations: false,
  guestAccountToCharge: null,
  noMemberAccessMessage: null,
  hideFromMembers: false,
  ignoreConflictingReservations: false,
  theme: null,
  extras: null,
  notes: null,
  onlineMessage: null,
  cancelled: false,
  closed: false,
  lottery: false,
  lotteryProcessed: null,
  lotteryApproved: null,
  discreteCovers: true,
  banquetEventOrder: null,
  questions: [],
  locations: [],
  seatings: [],
  costs: [],
  cascadeChangesTo: [],
  recordLockExpiration: null
}

export default {
  name: 'EventsItem',
  components: {QuestionEditor, MarkupEditor, BoundPropertySelector},
  mixins: [itemEdit, withChildren],
  data: function () {
    return {
      type: 'Event',
      types: 'Events',
      apiRecordCommand: 'diningEvent',
      itemDefault: itemDefault,
      members: [],
      showEventGroupSaveDialog: false,
      saveEventGroupModeTab: '1', // '0' = Save Single, '1' = Cascade changes
      saveItemCallback: null,
      eventCategories: [],
      eventTypes: [],
      timeFormats: [],
      dressCodes: [],
      musicalStyles: [],
      eventGroups: [],
      eventsInGroup: [],
      locations: [],
      tableConfigs: [],
      priceTypes: [],
      periods: [],
      guestTypes: [],
      posMenuItems: [],
      cancellationChargeTypes: [],
      cancellationTimeIntervals: [],
      memberRestrictions: [],
      guestAllowSelection: [],
      popStartEnd: '',
      sendNonMemberTicketsToPOS: false
    }
  },
  computed: {

    enablePOSChargeIntegration () {
      return this.$ftCS?.DINING_ENABLE_POS_CHARGE_INTEGRATION
    },

    cancellationPercentPosWarning () {
      return this.enablePOSChargeIntegration &&
        this.cancellationChargeType?.code === 'percent'
    },
    priceHeaders () {
      const headers = [
        this.headerDragSort(),
        this.headerEditor({
          label: 'Price Category',
          field: 'name',
          editor: {
            required: true,
            rules: this.rules().required()
          }
        }),
        this.headerEditor({
          label: 'Price',
          field: 'price',
          editor: {type: 'slot'}
        }),
        this.headerEditor({
          label: 'Price Type',
          field: 'priceTypeId',
          editor: {type: 'slot'}
        })
      ]
      if (this.enablePOSChargeIntegration) {
        headers.push(
          this.headerEditor({
            label: 'Charge Code',
            field: 'chargeCode',
            editor: {type: 'slot'}
          })
        )
      }

      headers.push(
        this.headerEditor({
          label: 'Hide Price',
          field: 'hidePrice',
          editor: {type: 'switch'},
          thin: true
        }),
        this.headerEditor({
          label: 'Visible To Members',
          field: 'allowMemberMade',
          editor: {type: 'switch'},
          thin: true
        }),
        this.headerEditor({
          label: 'Used By',
          field: 'useCount',
          editor: {type: 'slot'},
          thin: true
        })
      )

      return headers
    },
    allowAutoAssign () {
      return this.$ftCS.DINING_ENABLE_TABLE_AUTO_ASSIGN
    },
    console () {
      return console
    },
    cloner () {
      return this.$ftAccess.manageEvents ? cloner : null
    },
    forceReadOnly () {
      return !this.$ftAccess.manageEvents
    },
    selectedEventGroup () {
      return this.eventGroups && this.eventGroups.find(g => g.id === this.itemData.eventGroupId)
    },
    selectedDateIsInPast () {
      if (this.itemData.date) {
        const now = this.$ftAPI().clubMoment()
        const selectedDate = moment(this.itemData.date + ' 23:59:59')
        return selectedDate.isBefore(now)
      }
      return false
    },
    selectedDateIsInPastMessage () {
      if (!this.selectedDateIsInPast) return
      return 'Date is in the past'
    },
    minimumEventDate () {
      return this.$ftAPI().clubMoment().subtract(this.$ftCS.DINING_PAST_EVENT_MODIFY_DAYS, 'days').format('YYYY-MM-DD')
    },
    eventContextMenu () {
      if (!this.readOnly && this.showItem && !this.isNewItem) {
        const menuItems = [
          {hide: !this.editPanelActive}, // Divider
          {title: 'Configure Recurrence', icon: 'mdi-calendar-multiple', action: this.showRecurr, hide: !this.editPanelActive},
          {title: 'Send Event Charges', icon: 'mdi-cash-multiple', action: this.showEventBilling, hide: !this.editPanelActive}
        ]
        return this.defaultContextMenu.concat(menuItems)
      } else {
        return this.defaultContextMenu
      }
    },
    cancellationChargePercentage: {
      get () {
        const num = this.forceFloat(this.itemData.cancellationCharge)
        return isNaN(num) || num === null || num === '' ? num : +(num * 100).toFixed(2)
      },
      set (val) {
        this.itemData.cancellationCharge =
          isNaN(val) || val === null || val === ''
            ? val
            : +(val / 100).toFixed(4)
      }
    },
    cancellationChargeTypeId () {
      return this.itemData.cancellationChargeTypeId
    },
    cancellationChargeType () {
      return this.cancellationChargeTypes.find((v) => {return v.id === this.itemData.cancellationChargeTypeId}) || {}
    },
    hasAvailLocation () {
      return this.locations.filter((location) => {
        if (location.disabled) return false
        if (!this.itemData.locations) return true
        for (let locBinding of this.itemData.locations) {
          if (locBinding.locationId === location.id) return false
        }
        return true
      }).length > 0
    },
    getDisabledInverted () {
      return !this.itemData.disabled
    },
    timeFormatMap () {
      return this.timeFormats.reduce((s, tf) => {
        s[tf.id] = tf
        return s
      }, {})
    },
    cascadeEventChanges () {
      return !!parseInt(this.saveEventGroupModeTab, 10)
    }
  },
  watch: {
    sendNonMemberTicketsToPOS (val) {
      if (!val) {
        this.itemData.guestAccountToCharge = null
      }
    },
    cancellationChargeTypeId (newId, oldId) {
      const newType = this.cancellationChargeTypes.find(v => v.id === newId)

      if (!newType || newType.code !== 'flat') {
        this.itemData.cancellationChargeCode = null
      }
    },
    cancellationChargeType: {
      immediate: false,
      handler (newVal, oldVal) {
        if (!newVal || this.isLoading) return
        const oldCode = oldVal?.code
        const newCode = newVal.code
        const val = this.forceFloat(this.itemData.cancellationCharge)
        const hasNumber = val !== null && !isNaN(val)
        if (hasNumber && oldCode && oldCode !== newCode) {
          if (newCode === 'percent' && oldCode === 'flat') {
            this.itemData.cancellationCharge = val / 100
          }
          if (newCode === 'flat' && oldCode === 'percent') {
            this.itemData.cancellationCharge = val * 100
          }
        }
        if (newCode === 'flat' && this.itemData.cancellationChargeCode) {
          const posItem = this.posMenuItems.find(
            i => i.sku === this.itemData.cancellationChargeCode
          )
          if (posItem?.memberPrice != null) {
            this.itemData.cancellationCharge = this.forceCurrency(posItem.memberPrice)
          }
        }
      }
    },
    saveEventGroupModeTab (val) {
      // console.log('saveEventGroupModeTab', val, !!parseInt(val, 10))
    }
  },
  methods: {
    displayTime,
    displayDate,
    showRecurr () {
      this.promptReleaseLock(() => {
        this.$router.replace({path: '/events/recurring-event', query: {id: this.itemData.eventGroupId ? this.itemData.eventGroupId : '', beid: this.itemData.id}})
      })
    },
    onCancellationChargeCodeSelect (val) {
      this.clearPathErrors(this.jsonPath, 'cancellationChargeCode')
      if (!val) {
        this.itemData.cancellationCharge = null
        return
      }
      const posItem = this.posMenuItems.find(i => i.sku === val)
      if (!posItem) {
        this.itemData.cancellationCharge = null
        return
      }
      this.itemData.cancellationCharge = this.forceCurrency(posItem.memberPrice)
    },
    showEventBilling () {
      this.promptReleaseLock(() => {
        this.$router.replace({path: '/events/event-billing', query: {showId: this.itemData.id}})
      })
    },
    popStartEndDates () {
      this.popStartEnd = 'ft-pop-element'
      setTimeout(() => {this.popStartEnd = ''}, 250)
      this.clearPathErrors(this.jsonPath, 'date')
    },
    getTimeFormat (id) {
      return this.timeFormatMap[id] || {}
    },
    itemResourceCommands () {
      return [
        {command: 'diningLocation', load: this.locations, loading: this.isLoading},
        {command: 'diningTableConfiguration', load: this.tableConfigs, loading: this.isLoading, params: {include_fields: ['autoAssignable']}},
        {command: 'diningEventCategory', load: this.eventCategories, loading: this.isLoading},
        {command: 'diningEventType', load: this.eventTypes, loading: this.isLoading},
        {command: 'diningTimeFormat', load: this.timeFormats, loading: this.isLoading},
        {command: 'diningDressCode', load: this.dressCodes, loading: this.isLoading},
        {command: 'diningMusicalStyle', load: this.musicalStyles, loading: this.isLoading},
        {command: 'diningPriceType', load: this.priceTypes, loading: this.isLoading},
        {command: 'diningCancellationChargeType', load: this.cancellationChargeTypes, loading: this.isLoading},
        {command: 'diningCancellationTimeInterval', load: this.cancellationTimeIntervals, loading: this.isLoading},
        {command: 'diningPeriod', load: this.periods, loading: this.isLoading},
        {command: 'memberRestriction', load: this.memberRestrictions, loading: this.isLoading},
        {command: 'memberSearch', load: this.members, loading: this.isLoading},
        {command: 'guestType', load: this.guestTypes, loading: this.isLoading, params: {filter_methods: {filterActivityId: [9999]}}},
        ...this.enablePOSChargeIntegration
          ? [{
            command: 'posMenuItem',
            load: this.posMenuItems,
            loading: this.isLoading,
            params: {
              filter_methods: {
                filterActiveOnly: [],
                filterFieldNonNull: ['sku']
              }
            }
          }]
          : [],
        // {
        //   command: 'apiFieldOption',
        //   load: r => {
        //     this.guestAllowSelection = r
        //     this.itemDefault.guests = r[0].value
        //     if (this.itemData.guests === null) {
        //       this.itemData.guests = r[0].value
        //     }
        //   },
        //   loading: this.isLoading,
        //   params: {filter_methods: {forField: ['diningEvent', 'guests']}}
        // }
      ]
    },
    withChildrenItemCommandFilter (command) {
      this.addCommandParamArrayVal(command, 'include_fields', 'onlineMessage', 'diningEventCost.useCount')
      this.addCommandParam(command, 'include_fuzzy_hash', true)
      return command
    },
    afterLoad () {
      // Disable any locations that are in use so multiple of the same can't be added
      // this.disableSelectedLocations()
      this.showRecurrModal = false
      this.showEventCharges = false
      // Set defaults from settings
      if (this.isNewItem) {
        this.itemData.minAdvanceDays = this.$ftCS.DINING_EVENT_DEFAULT_MIN_DAYS_IN_ADVANCE
        this.itemData.maxAdvanceDays = this.$ftCS.DINING_EVENT_DEFAULT_MAX_DAYS_IN_ADVANCE
        this.itemData.maxPartySize = this.$ftCS.DINING_EVENT_DEFAULT_MAX_PARTY_SIZE
      }

      this.sendNonMemberTicketsToPOS = !!this.itemData.guestAccountToCharge
    },
    beforeSaveItem (saveItemCallback) {
      this.saveItemCallback = saveItemCallback
      console.log('Saving Event', this.itemData)
      if (!this.isNewItem && this.itemData.eventGroupId) {
        // We're part of an event group
        this.$ftAPI().get(this.addResourceErrorHandler([{
          command: 'diningEventGroup',
          load: (data) => {
            // Add selection / disabled options to list
            const eventGroup = data && data[0] || {events: []}
            const thisItem = eventGroup.events.find(e => e.id === this.itemData.id) || this.itemData
            const filteredEvents = eventGroup.events.map(e => {
              e.__selected = e.fuzzyHash === thisItem.fuzzyHash
              e.__sinkToBottom = e.fuzzyHash !== thisItem.fuzzyHash
              e.disabled = e.disabled || e.id === thisItem.id || e.fuzzyHash !== thisItem.fuzzyHash
              return e
            })
            // Set events we're part of
            this.eventsInGroup = filteredEvents.filter(e => !e.__sinkToBottom).concat(filteredEvents.filter(e => e.__sinkToBottom))
            // Set event Group we're part of
            this.eventGroups = data
            // Show dialog to select events we'll cascade changes to
            this.showEventGroupSaveDialog = true
            // Work-around for tabs slider issue
            this.$refs.saveEventGroupModeTabs && this.$refs.saveEventGroupModeTabs.callSlider()
          },
          loading: this.setIsLoading,
          params: {
            include_fuzzy_hash: true,
            children: true,
            filter: [{field: 'id', operator: 'EQUAL', values: [this.itemData.eventGroupId]}]
          }
        }]))
      } else {
        // Not part of event group.  Just save.
        this.saveWithoutCascade()
      }
    },
    saveEventByGroupMode () {
      this.showEventGroupSaveDialog = false
      if (this.cascadeEventChanges) {
        this.saveWithCascade()
      } else {
        this.saveWithoutCascade()
      }
    },
    saveWithoutCascade () {
      this.itemData.cascadeChangesTo = [] // Make sure we're not trying to cascade changes
      this.saveItemCallback()
    },
    saveWithCascade () {
      this.itemData.cascadeChangesTo = this.eventsInGroup.filter(e => e.__selected && !e.disabled).map(e => e.id)
      this.saveItemCallback()
    }
  }
}
</script>
<style>

.ft-pop-element {
  animation: popElement 0.2s linear 1;
}
@keyframes popElement {
  50%  {transform: scale(1.1)}
}

.fadeheight-enter-active,
.fadeheight-leave-active {
  transition: all 0.2s;
  max-height:500px;
}
.fadeheight-enter,
.fadeheight-leave-to
{
  max-height:0;
  opacity: 0;
  transform: scaleY(0);
}


</style>

